import { Star, Phone, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { CLINIC } from "@/lib/clinic";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 sm:pt-36 sm:pb-24 lg:pt-40 lg:pb-32 overflow-hidden"
    >
      {/* Soft background blobs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-20 h-[480px] w-[480px] rounded-full bg-[#E08570]/15 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-[#3A564A]/10 blur-3xl"
      />

      <div className="container mx-auto px-6 sm:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: Text */}
          <div className="lg:col-span-7 fade-up">
            <span
              data-testid="hero-overline"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#3A564A]"
            >
              <span className="h-px w-8 bg-[#3A564A]" />
              Dental clinic · Gomti Nagar
            </span>

            <h1
              data-testid="hero-heading"
              className="font-display font-bold text-stone-900 tracking-tight text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[1.02]"
            >
              Painless dentistry,
              <br />
              <span className="italic text-[#3A564A]">delivered with care.</span>
            </h1>

            <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-xl">
              Lucknow's most-loved family dental clinic — a warm,
              women-owned practice rated{" "}
              <span className="font-semibold text-stone-900">5.0★</span> by{" "}
              <span className="font-semibold text-stone-900">
                {CLINIC.reviewCount} patients
              </span>{" "}
              on Google. From cleanings to smile design, you're in expert,
              gentle hands.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`tel:${CLINIC.phoneTel}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#3A564A] hover:bg-[#2C4239] text-[#FAFAF9] text-sm font-semibold transition-all hover:scale-[1.02]"
              >
                <Phone className="w-4 h-4" />
                {CLINIC.phoneDisplay}
              </a>
              <a
                href="#visit"
                data-testid="hero-visit-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-stone-300 text-stone-900 text-sm font-semibold hover:bg-stone-50 hover:border-stone-400 transition-all"
              >
                Visit the Clinic <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Trust strip */}
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 fill-[#E08570] text-[#E08570]"
                  />
                ))}
                <span className="ml-2 text-sm font-semibold text-stone-900">
                  5.0
                </span>
                <span className="text-sm text-stone-500">
                  · {CLINIC.reviewCount} Google reviews
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-stone-600">
                <ShieldCheck className="w-4 h-4 text-[#3A564A]" />
                Sterilised, audited equipment
              </div>
              <div className="flex items-center gap-2 text-sm text-stone-600">
                <Sparkles className="w-4 h-4 text-[#3A564A]" />
                Women-owned
              </div>
            </div>
          </div>

          {/* Right: Image + floating review card */}
          <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-stone-200 shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1489278353717-f64c6ee8a4d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzbWlsaW5nJTIwd29tYW4lMjBwZXJmZWN0JTIwdGVldGglMjBkZW50aXN0fGVufDB8fHx8MTc3NzYzMjE0M3ww&ixlib=rb-4.1.0&q=85"
                alt="Bright, confident smile — patient at Dent Square"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/30 via-transparent to-transparent" />
            </div>

            {/* Floating Google review card */}
            <div
              data-testid="hero-review-card"
              className="absolute -bottom-6 -left-4 sm:-left-10 max-w-[280px] bg-white rounded-2xl border border-stone-200 shadow-xl p-5"
            >
              <div className="flex items-center gap-2">
                <div className="grid place-items-center w-8 h-8 rounded-full bg-[#3A564A] text-white text-xs font-bold">
                  G
                </div>
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-3.5 h-3.5 fill-[#E08570] text-[#E08570]"
                    />
                  ))}
                </div>
                <span className="ml-auto text-xs font-semibold text-stone-900">
                  5.0
                </span>
              </div>
              <p className="mt-3 text-sm text-stone-700 leading-snug">
                "Best dentist in Lucknow. Painless, professional and explains
                everything clearly."
              </p>
              <p className="mt-2 text-xs text-stone-500">— Sumit Kumar</p>
            </div>

            {/* small floating chip */}
            <div className="absolute -top-4 right-4 sm:right-8 bg-[#FAFAF9] border border-stone-200 rounded-full px-4 py-2 shadow-md">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#3A564A]">
                Open · Closes 9 pm
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
